import axios from 'api/axios-configure'
import { Grid } from '@mui/material'
import { AlertBanner, AlertBannerText, Button, Checkbox, Dropdown, TextField } from 'monday-ui-react-core'
import React, { useContext, useEffect, useState } from 'react'
import { ArrowLeft } from 'react-bootstrap-icons'
import { Link } from 'react-router-dom'
import { LoadingSpinner } from 'components/LoadingSpinner'
import ChangeOrganization from './ChangeOrganization'
import './OrganizationForm.scss'
import OrganizationLinks from './OrganizationLinks'
import { AuthSessionContext } from 'api/AuthSession'
import OrganizationSenderDetail from './OrganizationSenderDetail'
import { StatementContext } from 'utils/context/StatementsContext'
import OrganizationReceiverDetail from './OrganizationReceiverDetail'

const STRING_ONLY = /^[a-zA-Z\-_ ]+$/
const ALPHANUMERIC_ONLY = /^[a-zA-Z0-9_ ]+$/

const OrganizationForm = () => {
    const { setUserRole, IS_USER } = useContext(AuthSessionContext)
    const { isSender, getUserPermissionsSummary } = useContext(StatementContext)

    const [isLoading, setIsLoading] = useState(true)
    const [disableSave, setDisableSave] = useState(false)
    const [displayBanner, setDisplayBanner] = useState(false)
    const [bannerMessage, setBannerMessage] = useState('')
    const [bannerStyle, setBannerStyle] = useState('positive')
    const [changeOrganizationModal, setChangeOrganizationModal] = useState(false)

    const [organizationName, setOrganizationName] = useState('')
    const [organizationNumber, setOrganizationNumber] = useState('')
    const [isDefaultOrganization, setIsDefaultOrganization] = useState('')
    const [organization, setOrganization] = useState('')
    const [currentOrganization, setCurrentOrganization] = useState('')
    const [disableDefaultOrganization, setDisableDefaultOrganization] = useState(true)

    const [userOrganizations, setUserOrganizations] = useState([])
    const [isChangingOrg, setIsChangingOrg] = useState(false)

    const [viewReceiverInformation, setViewReceiverInformation] = useState({})
    const [displayReceiverDetail, setDisplayReceiverDetail] = useState(false)

    const validateStringOnly = (field) => !STRING_ONLY.test(field)
    const validateAlphaNumeric = (field) => !ALPHANUMERIC_ONLY.test(field)

    const clearBanner = () => {
        setDisplayBanner(false)
        setBannerMessage('')
        setBannerStyle('positive')
    }

    const displayErrorMessage = (message) => {
        setBannerMessage(message)
        setBannerStyle('negative')
        setDisplayBanner(true)
    }

    const displaySuccessMessage = (message) => {
        setBannerMessage(message)
        setBannerStyle('positive')
        setDisplayBanner(true)

        setTimeout(() => {
            clearBanner()
        }, 5000)
    }

    // Onload, get organization
    useEffect(() => {
        loadPage()
        getUserPermissionsSummary()
    }, [])

    useEffect(() => {
        // Display confirmation modal if the organizationId is a new one
        const organizationId = organization.value ?? ''
        const currentOrganizationId = currentOrganization.value ?? ''
        if (organizationId === currentOrganizationId) {
            return;
        } else {
            setChangeOrganizationModal(true)
        }
    }, [organization])

    const handleConfirmChangeOrganization = async () => {
        // Change organization and set local storage
        setChangeOrganizationModal(false)
        setCurrentOrganization(organization)

        localStorage.setItem('OrganizationId', organization.value)
        localStorage.setItem('OrganizationName', organization.label)

        window.dispatchEvent(new Event('OrganizationUpdated'))
        setIsChangingOrg(true)
        await getUserPermissionsSummary()
        axios.post('/UpdateOrgUserInfo')

        loadPage()
    }

    const loadPage = () => {
        setIsLoading(true)
        clearBanner()

        Promise.all(
            [axios.get('/GetOrganizationInfo'), 
            axios.get('/GetUserOrganizations')])
        .then(responses => {
            // Set list of user's organizations
            const orgs = responses[1].data
            setUserOrganizations(orgs.map((org, index) => {
                return {
                    value: org.id,
                    label: org.orgName
                }
            }))
            
            // Set org info
            const orgInfo = responses[0].data
            setOrganizationName(orgInfo.orgName)
            setOrganizationNumber(orgInfo.orgNumber)
            setIsDefaultOrganization(orgInfo.isDefault)
            setDisableDefaultOrganization(orgInfo.isDefault)
            setUserRole(orgInfo.role)
            const currentOrg = {
                value: orgInfo.id,
                label: orgInfo.orgName
            }
            setOrganization(currentOrg)
            setCurrentOrganization(currentOrg)

            if (isChangingOrg) 
                displaySuccessMessage('Organization changed successfully')

        })
        .catch(err => {
            console.error(err)
            if (isChangingOrg)
                displayErrorMessage('There was an error changing organization')
        })
        .finally(() => {
            setIsLoading(false)
            setIsChangingOrg(false)
        })
    }

    const handleDenyChangeOrganization = () => {
        setOrganization(currentOrganization)
        setChangeOrganizationModal(false)
    }

    const handleSave = () => {
        const payload = {
            id: organization.value,
            orgNumber: organizationNumber,
            orgName: organizationName,
            isDefault: isDefaultOrganization
        }

        setIsLoading(true)
        clearBanner()

        axios
            .post('/UpdateOrganizationInfo', payload)
            .then(response => {
                displaySuccessMessage('Organization data updated successfully')
                localStorage.setItem('OrganizationName', organizationName)
                window.dispatchEvent(new Event('OrganizationUpdated'))

                setDisableDefaultOrganization(isDefaultOrganization)
            })
            .catch(err => {
                console.error(err)
                displayErrorMessage('There was an error updating user information.')
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const handleReceiverDetailBack = () => {
        setDisplayReceiverDetail(false)
        setViewReceiverInformation({})
    }

    return (
        <>
            {
                !displayReceiverDetail ? (
                    <>
                        <div className="mx-2 form">
                        {
                            !isLoading ? (
                                <>
                                    <Grid container spacing={2} className="">
                                        <Grid item xs={12} style={{paddingTop: '0px'}}>
                                            <Link
                                                className="d-flex align-items-center"
                                                to="/dashboard"
                                            >
                                                <ArrowLeft size={13} />
                                                <span className="ml-2"> Back</span>
                                            </Link>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <h2>Organization</h2>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <h4>Current Organization</h4>
                                        </Grid>
                                        
                                        {displayBanner && (
                                            <Grid item xs={12}>
                                                <AlertBanner backgroundColor={bannerStyle} isCloseHidden>
                                                    <AlertBannerText text={bannerMessage} />
                                                </AlertBanner>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} lg={4}>
                                            <TextField
                                                id="organization-name"
                                                value={organizationName}
                                                onChange={(event) => setOrganizationName(event)}
                                                title={
                                                    <>
                                                        Organization Name<b style={{ color: 'red' }}>*</b>
                                                    </>
                                                }
                                                placeholder="Organization Name"
                                                size={TextField.sizes.SMALL}
                                                maxLength={128}
                                                validation={{
                                                    status:
                                                        organizationName == ''
                                                            ? ''
                                                            : validateAlphaNumeric(organizationName)
                                                            ? 'error'
                                                            : 'success',
                                                    text:
                                                        organizationName == ''
                                                            ? ''
                                                            : validateAlphaNumeric(organizationName)
                                                            ? 'Please enter a valid organization name'
                                                            : '',
                                                }}
                                                disabled={IS_USER}
                                            />
                                        </Grid>
                                        <Grid item lg={8} />
                                        <Grid item xs={12} lg={4}>
                                            <TextField
                                                id="organization-number"
                                                value={organizationNumber}
                                                onChange={(event) => setOrganizationNumber(event)}
                                                title={
                                                    <>
                                                        Organization Number
                                                    </>
                                                }
                                                placeholder="Organization Number"
                                                size={TextField.sizes.SMALL}
                                                maxLength={128} 
                                                disabled={true}
                                            />
                                        </Grid>
                                        <Grid item lg={8} />
                                        <Grid className="d-flex align-items-center" item xs={12} lg={4}>
                                            <Checkbox
                                                checked={isDefaultOrganization}
                                                onChange={(event) =>
                                                    setIsDefaultOrganization(event.target.checked)
                                                }
                                                inputProps={{ 'aria-label': 'controlled' }}
                                                disabled={disableDefaultOrganization}
                                            />
                                            <p>
                                                Default Organization
                                            </p>
                                        </Grid>
                                        <Grid item lg={8} />
                                        <Grid item xs={12} lg={4}>
                                            <label className='field-label'>
                                                Change current organization
                                            </label>
                                            <Dropdown
                                                size={Dropdown.size.SMALL}
                                                options={userOrganizations}
                                                onChange={setOrganization}
                                                clearable={false}
                                                value={organization}
                                            />
                                        </Grid>
                                        <Grid item lg={8} />
                                    </Grid>
                                    <Grid
                                        container
                                        direction="row"
                                        alignItems="center"
                                        className="mt-3"
                                    >
                                        {/* <Grid item mr={2}>
                                            <Button
                                                size={Button.sizes.SMALL}
                                                type={Button.types.BUTTON}
                                                onClick={handleCancel}
                                                className="cancel"
                                            >
                                                Cancel
                                            </Button>
                                        </Grid> */}
                                        <Grid item lg={3} />
                                        <Grid item>
                                            <Button
                                                size={Button.sizes.SMALL}
                                                type={Button.types.BUTTON}
                                                className="primary"
                                                onClick={handleSave}
                                                disabled={disableSave}
                                            >
                                                Save Changes
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    </>
                            ) : (
                                <Grid container spacing={2} className="">
                                    <Grid item xs={12}>
                                        <LoadingSpinner/>
                                    </Grid>
                                </Grid>
                            )
                        }

                            {isSender && (
                                <Grid container direction="row" mt={5}>
                                    <OrganizationSenderDetail 
                                        changedOrg={isChangingOrg}         
                                        setDisplayReceiverDetail={setDisplayReceiverDetail} 
                                        setViewReceiverInformation={setViewReceiverInformation} 
                                    />
                                </Grid>
                            )}

                            <Grid container direction="row" mt={5}>
                                <OrganizationLinks 
                                    changedOrg={isChangingOrg} 
                                />
                            </Grid>
                        </div>

                        {changeOrganizationModal && (
                            <ChangeOrganization 
                                open={changeOrganizationModal} 
                                onCancel={handleDenyChangeOrganization} 
                                onConfirm={handleConfirmChangeOrganization} 
                                newOrganizationName={organization.label}
                            />
                        )}
                    </>
                ) : (
                    <OrganizationReceiverDetail 
                        open={displayReceiverDetail} 
                        onBack={handleReceiverDetailBack}
                        senderName={viewReceiverInformation.senderName}
                        senderNumber={viewReceiverInformation.senderNumber}
                        documentType={viewReceiverInformation.documentType}
                    />
                )
            }
        </>
    )
}

export default OrganizationForm
